/**
 * AuthStage — layout split-screen editorial das telas de autenticação.
 *
 * Espelha a identidade Group WS (rebrand 2026): painel ESQUERDO claro (~57%)
 * com a cena isométrica de marca (grade de losangos + cards de vidro 3D +
 * malha de circuito com nós de brilho) e o manifesto "Hoje / Amanhã"; painel
 * DIREITO navy (~43%) com o formulário. Em telas < 768px o painel da esquerda
 * é ocultado. Cores 100% do design system (ciano WS + navy).
 *
 * Tipografia fluida (clamp) calibrada para casar com a referência (~40px nas
 * headlines, ~44px no heading do form) e cards dimensionados em vw → escalam
 * de forma responsiva e proporcional ao viewport.
 *
 * O conteúdo do painel esquerdo é "muro de marca" fixo — consistente entre
 * login, MFA, recuperação e convite.
 */
import type { ReactNode } from 'react';
import { Zap, Lightbulb, Rocket, Star } from 'lucide-react';
import { Logo } from '@/shared/ui/logo';

interface AuthStageProps {
  /** @deprecated mantido por compat — não é mais renderizado. */
  quote?: ReactNode;
  /** @deprecated mantido por compat — não é mais renderizado. */
  quoteCite?: string;
  /** @deprecated mantido por compat — não é mais renderizado. */
  metaLeft?: string;
  /** @deprecated mantido por compat — não é mais renderizado. */
  metaRight?: string;
  /** Conteúdo do formulário (lado direito do split). */
  children: ReactNode;
  /** Tag opcional ao lado do logo (ex.: "Platform"). */
  tag?: string;
  /** Largura máxima do form interno (default: 416px). */
  formMaxWidth?: string;
}

/** Ciano da marca WS (versão aprofundada, legível sobre o claro). */
const BRAND_CYAN = '#0E8194';
/** Navy de tinta para textos sobre o painel claro. */
const INK_NAVY = '#13243B';

/**
 * Valores da marca — cards de vidro flutuantes em arranjo diamante (espelha a
 * referência). `x`/`y` são o CENTRO do card em % do cluster (lado direito do
 * painel claro). Ordem do diamante: topo · esquerda · direita · base.
 */
const VALUES = [
  { icon: Zap, label: 'Agilidade', x: 50, y: 31 },
  { icon: Lightbulb, label: 'Criatividade', x: 30, y: 53 },
  { icon: Rocket, label: 'Inovação', x: 79, y: 56 },
  { icon: Star, label: 'Excelência', x: 53, y: 71 },
] as const;

/** Arestas do diamante que conectam os cards (índices em VALUES). */
const EDGES = [
  [0, 1],
  [0, 2],
  [1, 3],
  [2, 3],
  [1, 2],
] as const;

/** Traços de circuito saindo dos cards rumo às bordas (diagonais ~45°). */
const TRACES = [
  { from: 0, x: 72, y: 2 },
  { from: 0, x: 24, y: 4 },
  { from: 2, x: 99, y: 38 },
  { from: 1, x: 1, y: 26 },
  { from: 1, x: 2, y: 80 },
  { from: 3, x: 26, y: 99 },
  { from: 3, x: 72, y: 98 },
] as const;

/** Nós de brilho (pontos luminosos do circuito) nas pontas dos traços. */
const NODES = [
  { x: 72, y: 3 },
  { x: 99, y: 38 },
  { x: 1, y: 26 },
  { x: 2, y: 80 },
  { x: 26, y: 99 },
  { x: 72, y: 98 },
] as const;

/** Glints / faíscas finas espalhadas na malha. */
const SPARKLES = [
  { x: 86, y: 26, s: 7 },
  { x: 62, y: 40, s: 5 },
  { x: 38, y: 14, s: 6 },
  { x: 16, y: 70, s: 5 },
  { x: 90, y: 74, s: 7 },
  { x: 12, y: 90, s: 5 },
] as const;

export function AuthStage({ tag, formMaxWidth = '416px', children }: AuthStageProps) {
  return (
    <div className="relative min-h-dvh bg-bg">
      <div className="grid min-h-dvh w-full grid-cols-1 overflow-hidden md:grid-cols-[1.33fr_1fr]">
        {/* ── Painel esquerdo: cena isométrica + manifesto ───────────────── */}
        <aside
          className="relative hidden flex-col justify-between overflow-hidden p-10 md:flex lg:p-14 xl:p-[4.5rem]"
          style={{
            background:
              'linear-gradient(135deg, #F7FBFE 0%, #ECF3FB 42%, #DEEAF7 78%, #D6E4F4 100%)',
          }}
          aria-hidden="true"
        >
          {/* Grade isométrica (losangos) — imagem de fundo da cena */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="auth-iso"
                width="74"
                height="74"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <path
                  d="M74 0H0V74"
                  fill="none"
                  stroke={BRAND_CYAN}
                  strokeOpacity="0.10"
                  strokeWidth="1"
                />
              </pattern>
              <radialGradient id="auth-iso-fade" cx="62%" cy="48%" r="60%">
                <stop offset="0%" stopColor="#fff" stopOpacity="1" />
                <stop offset="58%" stopColor="#fff" stopOpacity="0.65" />
                <stop offset="100%" stopColor="#fff" stopOpacity="0" />
              </radialGradient>
              <mask id="auth-iso-mask">
                <rect width="100%" height="100%" fill="url(#auth-iso-fade)" />
              </mask>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="url(#auth-iso)"
              mask="url(#auth-iso-mask)"
            />
          </svg>

          {/* Halos suaves de luz por trás do cluster */}
          <div
            className="pointer-events-none absolute right-[4%] top-[16%] h-[58%] w-[56%] rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 68%)',
            }}
          />
          <div
            className="pointer-events-none absolute -right-16 top-[8%] h-72 w-72 rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(116,199,228,0.26) 0%, rgba(116,199,228,0) 70%)',
            }}
          />

          {/* Cluster de cards 3D + malha de circuito (decorativo) */}
          <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[56%] lg:block">
            {/* Traços e arestas do circuito */}
            <svg
              className="absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {TRACES.map(({ from, x, y }, i) => (
                <line
                  key={`t-${i}`}
                  x1={VALUES[from].x}
                  y1={VALUES[from].y}
                  x2={x}
                  y2={y}
                  stroke={BRAND_CYAN}
                  strokeOpacity="0.14"
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {EDGES.map(([a, b]) => (
                <line
                  key={`e-${a}-${b}`}
                  x1={VALUES[a].x}
                  y1={VALUES[a].y}
                  x2={VALUES[b].x}
                  y2={VALUES[b].y}
                  stroke={BRAND_CYAN}
                  strokeOpacity="0.22"
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>

            {/* Nós de brilho do circuito */}
            {NODES.map(({ x, y }, i) => (
              <span
                key={`n-${i}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  width: 6,
                  height: 6,
                  background: '#fff',
                  boxShadow: `0 0 0 2px rgba(14,129,148,0.18), 0 0 9px 2px rgba(14,129,148,0.55)`,
                }}
              />
            ))}

            {/* Glints / faíscas */}
            {SPARKLES.map(({ x, y, s }, i) => (
              <span
                key={`s-${i}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  width: s,
                  height: s,
                  background:
                    'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 70%)',
                }}
              />
            ))}

            {/* Cards de vidro 3D — tiles flutuantes em diamante */}
            {VALUES.map(({ icon: Icon, label, x, y }) => (
              <div
                key={label}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  width: 'clamp(112px, 9.8vw, 156px)',
                  height: 'clamp(112px, 9.8vw, 156px)',
                }}
              >
                {/* Face lateral (espessura do bloco de vidro) */}
                <div
                  className="absolute inset-x-[7%] top-[16%] rounded-[22px]"
                  style={{
                    bottom: '-11%',
                    background:
                      'linear-gradient(180deg, rgba(173,201,231,0.60) 0%, rgba(143,180,222,0.32) 100%)',
                    filter: 'blur(0.5px)',
                  }}
                />
                {/* Face superior (vidro fosco) */}
                <div
                  className="relative flex h-full w-full flex-col items-center justify-center rounded-[22px] border border-white/80"
                  style={{
                    gap: 'clamp(8px, 0.7vw, 13px)',
                    background:
                      'linear-gradient(160deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.50) 100%)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    boxShadow:
                      '0 30px 56px -22px rgba(16,74,116,0.42), inset 0 1px 0 0 rgba(255,255,255,0.75)',
                  }}
                >
                  <Icon
                    style={{
                      color: BRAND_CYAN,
                      width: 'clamp(28px, 2.4vw, 40px)',
                      height: 'clamp(28px, 2.4vw, 40px)',
                    }}
                    strokeWidth={1.5}
                  />
                  <span
                    className="font-medium tracking-[0.01em] text-[#1B3B5A]"
                    style={{ fontSize: 'clamp(12.5px, 1vw, 15.5px)' }}
                  >
                    {label}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Brand block — monograma ciano + wordmark navy */}
          <div className="relative z-[1]">
            <Logo
              size="lg"
              tag={tag}
              monogramColor={BRAND_CYAN}
              className="!text-[#13243B]"
            />
          </div>

          {/* Manifesto editorial — Hoje / Amanhã */}
          <div className="relative z-[1]">
            <p
              className="mb-3 font-mono font-semibold uppercase"
              style={{
                color: BRAND_CYAN,
                fontSize: 'clamp(11px, 0.85vw, 13px)',
                letterSpacing: '0.24em',
              }}
            >
              Hoje
            </p>
            <h2
              className="font-editorial font-semibold tracking-[-0.02em]"
              style={{
                color: INK_NAVY,
                fontSize: 'clamp(1.55rem, 2.45vw, 2.5rem)',
                lineHeight: 1.1,
              }}
            >
              Entregamos
              <br />
              uma <span style={{ color: BRAND_CYAN }}>solução completa.</span>
            </h2>

            <span
              className="my-7 block h-px"
              style={{ width: 44, backgroundColor: INK_NAVY, opacity: 0.35 }}
            />

            <p
              className="mb-3 font-mono font-semibold uppercase"
              style={{
                color: BRAND_CYAN,
                fontSize: 'clamp(11px, 0.85vw, 13px)',
                letterSpacing: '0.24em',
              }}
            >
              Amanhã
            </p>
            <h2
              className="font-editorial font-semibold tracking-[-0.02em]"
              style={{
                color: INK_NAVY,
                fontSize: 'clamp(1.55rem, 2.45vw, 2.5rem)',
                lineHeight: 1.1,
              }}
            >
              Estamos construindo
              <br />
              uma nova forma
              <br />
              de <span style={{ color: BRAND_CYAN }}>viver.</span>
            </h2>
          </div>

          {/* Tagline rodapé */}
          <p
            className="relative z-[1]"
            style={{
              color: '#5B6B7E',
              maxWidth: 'clamp(260px, 22vw, 340px)',
              fontSize: 'clamp(13px, 1vw, 15.5px)',
              lineHeight: 1.6,
            }}
          >
            Tecnologia, inovação e excelência para criar experiências que
            transformam a vida das pessoas.
          </p>
        </aside>

        {/* ── Painel direito: formulário (navy) ──────────────────────────── */}
        <section
          className="flex flex-col justify-center px-8 py-12 md:px-12 md:py-14 lg:px-16 xl:px-[5.5rem]"
          style={{ background: 'linear-gradient(180deg, #0A2034 0%, #061828 100%)' }}
        >
          <div className="mx-auto w-full" style={{ maxWidth: formMaxWidth }}>
            {children}
          </div>
        </section>
      </div>
    </div>
  );
}
